import axios from 'axios';

/**
 * Service for managing pricing tiers and billing-related API calls
 */
const pricingService = {
  /**
   * Fetch all active pricing tiers from the database
   * @param {boolean} bypassCache - Whether to bypass browser cache 
   * @returns {Promise<Array>} Array of pricing tiers
   * @throws {Error} If pricing tiers cannot be retrieved from any endpoint
   */
  async getPricingTiers(bypassCache = true) {
    console.log('pricingService.getPricingTiers called with bypassCache:', bypassCache);
    
    // Add timestamp to prevent caching
    const cacheBuster = bypassCache ? `?_=${Date.now()}` : '';
    
    // First try the main public endpoint
    try {
      console.log(`Attempting to fetch from /api/pricing-tiers${cacheBuster}`);
      const response = await axios.get(`/api/pricing-tiers${cacheBuster}`);
      console.log('Pricing tiers response:', response);
      
      if (response.data && response.data.status === 'success' && Array.isArray(response.data.data) && response.data.data.length > 0) {
        console.log('Successfully retrieved pricing tiers from main endpoint');
        return response.data.data;
      } else {
        console.warn('Invalid response format from primary endpoint:', response.data);
        throw new Error('Invalid response format from primary endpoint');
      }
    } catch (primaryError) {
      console.warn('Error fetching from primary endpoint:', primaryError);
      
      // If the main endpoint fails, try the billing endpoint as fallback
      try {
        console.log(`Attempting to fetch from /api/billing/pricing-tiers${cacheBuster}`);
        const fallbackResponse = await axios.get(`/api/billing/pricing-tiers${cacheBuster}`);
        
        if (fallbackResponse.data && fallbackResponse.data.status === 'success' && Array.isArray(fallbackResponse.data.data) && fallbackResponse.data.data.length > 0) {
          console.log('Successfully retrieved pricing tiers from fallback endpoint');
          return fallbackResponse.data.data;
        } else {
          console.warn('Invalid response format from fallback endpoint:', fallbackResponse.data);
          throw new Error('Invalid response format from fallback endpoint');
        }
      } catch (fallbackError) {
        console.error('Fallback endpoint also failed:', fallbackError);
        
        // Try debug endpoint as last resort
        try {
          console.log(`Attempting to fetch from debug endpoint as last resort${cacheBuster}`);
          const debugResponse = await axios.get(`/api/debug/pricing-tiers${cacheBuster}`);
          
          if (debugResponse.data && debugResponse.data.status === 'success') {
            // If we have database tiers, use those
            if (Array.isArray(debugResponse.data.dbTiers) && debugResponse.data.dbTiers.length > 0) {
              console.log('Using database tiers from debug endpoint');
              return debugResponse.data.dbTiers;
            }
            
            // No valid data from any source
            console.error('No valid pricing tiers available from any source');
            throw new Error('No pricing tiers available from database');
          } else {
            throw new Error('Debug endpoint also failed');
          }
        } catch (debugError) {
          console.error('All endpoints failed:', debugError);
          throw new Error('Failed to retrieve pricing tiers from database');
        }
      }
    }
  },

  /**
   * Fetch a specific pricing tier by name
   * @param {string} name - The name of the pricing tier (e.g., 'free', 'professional', 'enterprise')
   * @returns {Promise<Object>} The pricing tier object
   */
  async getPricingTier(name) {
    try {
      const response = await axios.get(`/api/admin/pricing-tiers/${name}`);
      if (response.data.status === 'success') {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch pricing tier');
    } catch (error) {
      console.error(`Error fetching pricing tier ${name}:`, error);
      throw error;
    }
  },

  /**
   * Update a pricing tier (admin only)
   * @param {Object} tierData - The updated pricing tier data
   * @returns {Promise<Object>} The updated pricing tier
   */
  async updatePricingTier(tierData) {
    try {
      const response = await axios.put(`/api/admin/pricing-tiers/${tierData.name}`, tierData);
      if (response.data.status === 'success') {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to update pricing tier');
    } catch (error) {
      console.error('Error updating pricing tier:', error);
      throw error;
    }
  },

  /**
   * Format currency amount in cents to display format
   * @param {number} cents - Amount in cents
   * @param {Object} options - Formatting options
   * @returns {string} Formatted currency string
   */
  formatCurrency(cents, options = {}) {
    const { 
      currency = 'EUR', 
      minimumFractionDigits = 0,
      maximumFractionDigits = 2
    } = options;
    
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits,
      maximumFractionDigits
    });
  }
};

export default pricingService; 